/**
 * @file @ 下拉：看得见的知识库加载文档；搜索换列表时同步扫一遍可见项
 */
import { useEffect, useRef } from 'react';
import type { IKnowledgeDocItem } from './useKnowledgeBaseApi';

interface IParams {
  enabled: boolean;
  listEl: HTMLDivElement | null;
  /** 搜索 / 渲染列表变化 */
  observeKey: string;
  docsByKbId: Record<string, IKnowledgeDocItem[]>;
  loadKbDocs: (ids: string[]) => void;
}

/**
 * 可见未加载的 kbId
 */
const collectVisibleUnloadedKbIds = (
  listEl: HTMLDivElement,
  docsByKbId: Record<string, IKnowledgeDocItem[]>,
): string[] => {
  const rootRect = listEl.getBoundingClientRect();
  const ids: string[] = [];
  listEl.querySelectorAll<HTMLElement>('[data-kb-id]').forEach((node) => {
    const kbId = node.dataset.kbId || '';
    if (!kbId || docsByKbId[kbId] !== undefined) {
      return;
    }
    const rect = node.getBoundingClientRect();
    if (rect.bottom > rootRect.top && rect.top < rootRect.bottom) {
      ids.push(kbId);
    }
  });
  return ids;
};

/**
 * 搜索/列表变化：同步加载当前可见库；滚动用 IntersectionObserver
 */
export const useKbAtScrollDocLoader = ({
  enabled,
  listEl,
  observeKey,
  docsByKbId,
  loadKbDocs,
}: IParams): void => {
  const docsRef = useRef(docsByKbId);
  docsRef.current = docsByKbId;
  const loadRef = useRef(loadKbDocs);
  loadRef.current = loadKbDocs;

  useEffect(() => {
    if (!enabled || !listEl) {
      return undefined;
    }

    const loadVisible = () => {
      const ids = collectVisibleUnloadedKbIds(listEl, docsRef.current);
      if (ids.length) {
        loadRef.current(ids);
      }
    };

    loadVisible();

    const observer = new IntersectionObserver(
      (entries) => {
        const ids = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => (entry.target as HTMLElement).dataset.kbId || '')
          .filter((kbId) => kbId && docsRef.current[kbId] === undefined);
        if (ids.length) {
          loadRef.current(ids);
        }
      },
      { root: listEl, threshold: 0 },
    );

    listEl.querySelectorAll<HTMLElement>('[data-kb-id]').forEach((node) => {
      observer.observe(node);
    });

    return () => {
      observer.disconnect();
    };
  }, [enabled, listEl, observeKey]);
};
